import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, CreditCard, Calendar, ArrowUpRight, ArrowDownRight,
  XCircle, RefreshCcw, ExternalLink, Clock, ShieldCheck, AlertTriangle,
} from "lucide-react";
import { UniHeader } from "@/pages/university/_lmsShared";
import { api } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/launch`;
const AUTH_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Self-contained self-service cancellation card.
 * Reads /api/me/billing (admin JWT), displays current plan + paid_through_at,
 * requires an explicit confirmation dialog before submitting cancellation.
 */
function CancellationCard() {
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const submittedRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!localStorage.getItem("kcis_admin_token")) { setLoading(false); return; }
    try {
      const r = await api.get(`${AUTH_API}/me/billing`);
      setMe(r.data || null);
    } catch (_e) { setMe(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const sub = me?.subscription || null;
  const status = (sub?.status || me?.subscription_status || "").toLowerCase();
  const tier = (sub?.tier || sub?.plan_key || "").toLowerCase();
  const paidThrough = sub?.paid_through_at || sub?.current_period_end || me?.paid_through_at || "";
  const monthly = sub?.monthly_amount_usd || (tier === "pro" ? 149 : tier === "lite" ? 49 : "");
  const canCancel = status === "active" && !sub?.cancel_at_period_end;
  const alreadyScheduled = !!sub?.cancel_at_period_end;

  const openDialog = async () => {
    setError("");
    if (paidThrough) { setDlgOpen(true); return; }
    // Missing paid_through — attempt a verified Square lookup via admin
    // reconciliation (super_admin only). If unauthorised or lookup fails,
    // block cancellation and show a support message.
    setVerifying(true);
    try {
      const r = await api.post(`${AUTH_API}/admin/billing/reconcile-now?email=${encodeURIComponent(me?.email || "")}`);
      await refresh();
      const fresh = (r.data && r.data.checked > 0);
      if (!fresh || !(sub?.paid_through_at || sub?.current_period_end)) {
        setError("We could not verify your paid-through date. Please contact support before cancelling.");
        return;
      }
      setDlgOpen(true);
    } catch (_e) {
      setError("We could not verify your paid-through date. Please contact support before cancelling.");
    } finally { setVerifying(false); }
  };

  const submitCancel = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setBusy(true); setError("");
    try {
      const r = await api.post(`${AUTH_API}/billing/cancel-subscription`);
      setResult(r.data || {});
      setDlgOpen(false);
      await refresh();
    } catch (e) {
      setError("Your subscription could not be cancelled right now. Please try again or contact support.");
      submittedRef.current = false;
    } finally { setBusy(false); }
  };

  if (loading) return null;
  if (!me) return null;
  // Super admins have no personal subscription
  if (me.is_super_admin) return null;

  return (
    <div data-testid="practitioner-billing-cancel-card"
          className="max-w-3xl mx-auto mt-6 mb-8 rounded-2xl bg-white shadow-md p-6"
          style={{ borderTop: "3px solid #4A1D5E" }}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-purple-900 font-bold mb-1">Your subscription</div>
          <div className="text-xl font-serif">
            {tier === "pro" ? "KCIS Pro" : tier === "lite" ? "KCIS Lite" : "No active plan"}
            {monthly ? (<span className="text-slate-500 text-base"> · ${monthly}/mo</span>) : null}
          </div>
        </div>
        <div className="text-sm">
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold"
                 style={{ background: status === "active" ? "#DCFCE7" : "#FEE2E2",
                            color: status === "active" ? "#166534" : "#991B1B" }}
                 data-testid="practitioner-billing-status">
            {alreadyScheduled ? "Cancellation Scheduled" : (status || "unknown")}
          </span>
        </div>
      </div>
      {paidThrough && (
        <div className="text-sm text-slate-600 flex items-center gap-2 mb-2" data-testid="practitioner-billing-paid-through">
          <Calendar size={14} />
          {alreadyScheduled
            ? <>Your subscription will end on <b>{new Date(paidThrough).toLocaleDateString()}</b>. You will retain KCIS access through that date.</>
            : <>Next billing / paid through: <b>{new Date(paidThrough).toLocaleDateString()}</b></>}
        </div>
      )}
      {error && (
        <div className="mt-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800 flex gap-2 items-start" data-testid="practitioner-billing-cancel-error">
          <AlertTriangle size={16} className="mt-0.5" /> <div>{error}</div>
        </div>
      )}
      {result && (
        <div className="mt-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex gap-2 items-start"
              data-testid="practitioner-billing-cancel-success">
          <CheckCircle2 size={16} className="mt-0.5" />
          <div>
            Your subscription is scheduled to end on <b>{result.canceled_date ? new Date(result.canceled_date).toLocaleDateString() : (paidThrough ? new Date(paidThrough).toLocaleDateString() : "the paid-through date")}</b>. You will retain KCIS access through that date.
          </div>
        </div>
      )}
      {canCancel && !result && (
        <button onClick={openDialog} disabled={verifying}
                 data-testid="practitioner-billing-cancel-btn"
                 className="mt-3 px-4 py-2 rounded-lg font-semibold text-white flex items-center gap-2 disabled:opacity-50"
                 style={{ background: "#991B1B" }}>
          {verifying ? <Loader2 className="animate-spin" size={16} /> : <XCircle size={16} />}
          {verifying ? "Verifying paid-through date…" : "Cancel Subscription"}
        </button>
      )}

      {dlgOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
              data-testid="practitioner-billing-cancel-dialog">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <h3 className="text-lg font-serif mb-3">Cancel your KCIS subscription?</h3>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">
              Cancelling stops future monthly charges. You will keep KCIS access
              through <b>{new Date(paidThrough).toLocaleDateString()}</b>. After
              that date your protected practitioner tools will lock, but all
              client records, assessments, reports, and protocol history are
              preserved and remain accessible if you reactivate later.
            </p>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setDlgOpen(false)} disabled={busy}
                       data-testid="practitioner-billing-cancel-dismiss"
                       className="px-3 py-2 text-sm rounded-lg text-slate-700 hover:bg-slate-100">Keep my subscription</button>
              <button onClick={submitCancel} disabled={busy}
                       data-testid="practitioner-billing-cancel-confirm"
                       className="px-3 py-2 text-sm rounded-lg font-semibold text-white flex items-center gap-2 disabled:opacity-50"
                       style={{ background: "#991B1B" }}>
                {busy ? <Loader2 className="animate-spin" size={14} /> : <XCircle size={14} />}
                {busy ? "Cancelling…" : "Cancel subscription"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PractitionerBilling() {
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState("");
  const [pendingCheckoutUrl, setPendingCheckoutUrl] = useState("");
  const [email, setEmail] = useState(localStorage.getItem("kcis_practitioner_email") || "");
  const pollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const p = await axios.get(`${API}/plans`); setPlans(p.data?.plans || []);
      if (email) {
        const s = await axios.get(`${API}/subscriptions/by-email/${encodeURIComponent(email)}`);
        const sub = s.data?.subscription || null;
        setSub(sub);
        if (sub?.status === "square_pending" && sub?.square_payment_url) {
          setPendingCheckoutUrl(sub.square_payment_url);
        } else {
          setPendingCheckoutUrl("");
        }
      } else {
        setSub(null); setPendingCheckoutUrl("");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [email]);

  // Poll every 6s while subscription is pending — webhook flips it to active
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (sub?.status === "square_pending") {
      pollRef.current = setInterval(load, 6000);
      return () => { if (pollRef.current) clearInterval(pollRef.current); };
    }
    // eslint-disable-next-line
  }, [sub?.status]);

  const start = async (plan_key) => {
    if (!email) return toast.error("Enter your practitioner email first.");
    setStarting(plan_key);
    try {
      const r = await axios.post(`${API}/subscriptions/start`, {
        plan_key, practitioner_email: email, practitioner_name: "",
      });
      localStorage.setItem("kcis_practitioner_email", email);
      const newSub = r.data.subscription;
      const checkoutUrl = r.data.checkout_url || newSub.square_payment_url || "";
      setSub(newSub);
      setPendingCheckoutUrl(checkoutUrl);
      if (checkoutUrl) {
        toast.success(`${newSub.plan_name} — pay now to activate`);
        window.open(checkoutUrl, "_blank", "noopener,noreferrer");
      } else {
        toast.warning("Subscription created in pending state. Payment link unavailable — contact support.");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start subscription"); }
    finally { setStarting(""); }
  };
  const change = async (new_key) => {
    try {
      const r = await axios.post(`${API}/subscriptions/${sub.id}/change`, { new_plan_key: new_key });
      toast.success(`${r.data.direction}d to ${r.data.subscription.plan_name}`);
      setSub(r.data.subscription);
    } catch (e) { toast.error(e?.response?.data?.detail || "Change failed"); }
  };
  const cancel = async () => {
    if (!confirm("Cancel subscription? You'll keep access until the period ends.")) return;
    try {
      await axios.post(`${API}/subscriptions/${sub.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error("Cancel failed"); }
  };
  const reactivate = async () => {
    try {
      const r = await axios.post(`${API}/subscriptions/${sub.id}/reactivate`);
      toast.success("Reactivated"); setSub(r.data.subscription);
    } catch (e) { toast.error("Reactivate failed"); }
  };

  const isPending = sub?.status === "square_pending";
  const isActive = sub?.status === "active";
  const statusStyle = isActive ? "text-emerald-700"
    : isPending ? "text-amber-700"
    : "text-slate-700";

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <UniHeader title="Practitioner · Subscription Billing" back="/practitioner" />
      <CancellationCard />
      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="practitioner-billing-page">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 mb-6 text-xs text-emerald-900 flex items-start gap-2" data-testid="pb-prod-banner">
          <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Production-safe initial activation:</b> Practitioner access is locked until Square confirms your first-month payment via webhook.
            <span className="block mt-1 opacity-80">Recurring auto-renewal (Square Subscriptions API) ships next sprint — you'll re-confirm payment at the end of each 30-day cycle until then.</span>
          </div>
        </div>

        {/* Email gate */}
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6 flex items-center gap-3">
          <CreditCard className="w-4 h-4 text-purple-900" />
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your practitioner email"
            className="flex-1 rounded-xl border border-slate-200 h-9 px-3 text-sm" data-testid="pb-email" />
          <button onClick={load} className="text-xs text-purple-900 hover:underline inline-flex items-center gap-1" data-testid="pb-reload"><RefreshCcw className="w-3 h-3" />Load</button>
        </div>

        {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-500" /> : (
          <>
            {/* Pending payment banner with Pay Now */}
            {isPending && (
              <section className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 mb-6" data-testid="pb-pending">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-700 mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="text-amber-900 font-semibold text-base">Payment pending — complete to activate</div>
                    <div className="text-sm text-amber-800 mt-1">
                      Your <b>{sub.plan_name}</b> subscription (${sub.monthly_amount_usd}/mo) is reserved in
                      <span className="font-mono text-xs"> square_pending</span> state. Practitioner access remains locked
                      until Square confirms your ${sub.monthly_amount_usd} first-month charge.
                    </div>
                    {pendingCheckoutUrl ? (
                      <a href={pendingCheckoutUrl} target="_blank" rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-700 hover:bg-amber-800 text-white h-10 px-4 text-sm font-medium"
                        data-testid="pb-pay-now">
                        <CreditCard className="w-4 h-4" />Pay ${sub.monthly_amount_usd} Now
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <div className="mt-4 text-xs text-amber-800 italic">Payment link unavailable — please contact admissions@kissedbycvon.com.</div>
                    )}
                    <div className="text-[11px] text-amber-700 mt-3">
                      This page refreshes automatically every 6 seconds. Once payment is confirmed via Square webhook,
                      your subscription will activate and access will unlock.
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Current subscription card (active / cancelled / abandoned) */}
            {sub && !isPending && (
              <section className="rounded-2xl border border-slate-200 bg-white p-5 mb-6" data-testid="pb-current">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Current Plan</div>
                    <div className="font-display text-2xl text-slate-900 mt-0.5">{sub.plan_name}</div>
                    <div className="text-sm text-slate-600">
                      ${sub.monthly_amount_usd}/month · status <b className={statusStyle} data-testid="pb-status">{(sub.status || "").toUpperCase()}</b>
                      {isActive && sub.access_unlocked && (
                        <span className="ml-2 inline-flex items-center gap-1 text-emerald-700 text-xs"><ShieldCheck className="w-3 h-3" />Access unlocked</span>
                      )}
                    </div>
                    {isActive && (
                      <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />Renews {(sub.current_period_end || "").slice(0,10)}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isActive && sub.plan_key !== "enterprise" && (
                      <button onClick={() => change(sub.plan_key === "lite" ? "pro" : "enterprise")} className="rounded-xl bg-purple-900 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="pb-upgrade">
                        <ArrowUpRight className="w-3.5 h-3.5" />Upgrade
                      </button>
                    )}
                    {isActive && sub.plan_key !== "lite" && (
                      <button onClick={() => change(sub.plan_key === "enterprise" ? "pro" : "lite")} className="rounded-xl border border-slate-300 h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="pb-downgrade">
                        <ArrowDownRight className="w-3.5 h-3.5" />Downgrade
                      </button>
                    )}
                    {isActive ? (
                      <button onClick={cancel} className="rounded-xl border border-rose-300 text-rose-700 h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="pb-cancel"><XCircle className="w-3.5 h-3.5" />Cancel</button>
                    ) : sub.status === "cancelled" ? (
                      <button onClick={reactivate} className="rounded-xl bg-emerald-700 text-white h-9 px-3 text-sm inline-flex items-center gap-1.5" data-testid="pb-reactivate"><CheckCircle2 className="w-3.5 h-3.5" />Reactivate</button>
                    ) : null}
                  </div>
                </div>
                {sub.history?.length > 0 && (
                  <div className="mt-4" data-testid="pb-history">
                    <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Payment & event history</div>
                    <ul className="text-xs text-slate-600 space-y-1">
                      {sub.history.slice().reverse().map((h, i) => (
                        <li key={i}>
                          · {(h.event || "").toUpperCase()}
                          {h.from ? ` (${h.from} → ${h.to})` : ""}
                          {h.amount_usd ? ` · $${h.amount_usd}` : ""}
                          {h.square_payment_id ? ` · pmt ${h.square_payment_id.slice(0, 12)}…` : ""}
                          <span className="text-slate-400 ml-1">{(h.at || "").slice(0,16).replace("T", " ")}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {/* Plans grid */}
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="pb-plans">
              {plans.map((p) => {
                const current = sub?.plan_key === p.key && isActive;
                const disabled = isPending && sub?.plan_key === p.key;
                return (
                  <div key={p.key} className={`rounded-2xl border p-6 ${current ? "border-purple-900 bg-purple-50/40" : "border-slate-200 bg-white"}`} data-testid={`pb-plan-${p.key}`}>
                    <div className="text-[10px] uppercase tracking-wide text-purple-900 mb-2">{p.key === "pro" ? "Most Popular" : "\u00A0"}</div>
                    <div className="font-display text-2xl text-slate-900">{p.name}</div>
                    <div className="mt-2 text-3xl font-display text-slate-900">${p.monthly_amount_usd}<span className="text-sm text-slate-500">/mo</span></div>
                    <ul className="mt-5 space-y-1.5 text-sm text-slate-700">
                      {p.features.map((f, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>)}
                    </ul>
                    <div className="mt-6">
                      {current ? (
                        <div className="rounded-xl bg-purple-100 text-purple-900 h-10 inline-flex items-center px-3 text-sm">Current plan</div>
                      ) : disabled ? (
                        <div className="rounded-xl bg-amber-100 text-amber-900 h-10 inline-flex items-center px-3 text-sm gap-1.5"><Clock className="w-3.5 h-3.5" />Payment pending</div>
                      ) : isActive ? (
                        <button onClick={() => change(p.key)} className="rounded-xl border border-slate-300 h-10 w-full text-sm" data-testid={`pb-change-${p.key}`}>Switch to {p.name}</button>
                      ) : (
                        <button onClick={() => start(p.key)} disabled={starting === p.key || isPending}
                          className="rounded-xl bg-purple-900 hover:bg-purple-800 text-white h-10 w-full text-sm disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
                          data-testid={`pb-start-${p.key}`}>
                          {starting === p.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start {p.name}</>}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
